import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticatedFetch } from '../utils/api';
import { showSuccessToast, showErrorToast } from '../utils/toast';
import MetricCard from './shared/MetricCard';
import OptimizationProgress from './OptimizationProgress';
import CreditsWidget from './shared/CreditsWidget';
import useDashboardData from '../hooks/useDashboardData';

// ─── Dashboard ───────────────────────────────────────────────────────────────

// How often the dashboard asks for the job list (the per-job progress card polls on its own, faster)
const ACTIVE_JOBS_POLL_MS = 10000;

function Dashboard() {
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const authenticatedFetchRef = useRef(authenticatedFetch);
  useEffect(() => { authenticatedFetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const {
    stats, syncData, statsLoading, syncLoading,
    syncPolling, error, clearError, refreshStats, handleResyncProducts, handlePauseSync, handleResumeSync,
  } = useDashboardData();

  const [optimizing, setOptimizing] = useState(false);
  // Bumped whenever a job item newly fails — a failed optimize/reoptimize attempt refunds
  // its credit (see backend jobService.js), so the credits widget needs an immediate
  // re-fetch instead of waiting for its own ~20s poll.
  const [creditsRefreshKey, setCreditsRefreshKey] = useState(0);
  const handleCreditChange = useCallback(() => setCreditsRefreshKey((k) => k + 1), []);

  const { products, images, savings, lifetime } = stats || {};

  // ── Start optimization ──────────────────────────────────────────────────
  const handleStartOptimization = useCallback(async () => {
    try {
      setOptimizing(true);
      const response = await authenticatedFetchRef.current('/api/optimize', {
        method: 'POST',
        body: JSON.stringify({ selectAll: true, optimizationType: 'regular' }),
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 402 && data.errorCode === 'INSUFFICIENT_CREDITS') {
          showErrorToast(data.message || 'No credits remaining. Purchase more credits to continue optimizing.');
        } else {
          showErrorToast(data.code === 'MAX_JOBS_RUNNING'
            ? (data.message || 'Maximum 2 optimization jobs can run simultaneously.')
            : (data.message || 'Failed to start optimization'));
        }
        return;
      }

      showSuccessToast('Optimization job started successfully');
      window.dispatchEvent(new CustomEvent('jobStarted', { detail: { jobId: data.jobId } }));

      setTimeout(() => {
        document.getElementById('dashboard-jobs-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    } catch (err) {
      showErrorToast(err.message || 'Failed to start optimization');
    } finally {
      setOptimizing(false);
    }
  }, []);

  // ── Render helpers ──────────────────────────────────────────────────────
  const hasSyncedProducts = (syncData?.totalSynced || 0) > 0;
  const optimizedCount = products?.optimized || 0;
  const unoptimizedCount = products?.unoptimized || 0;
  const unoptimizedImageCount = images?.unoptimized || 0;

  // ── Credit pre-check before optimizing everything that is left ──────────
  // Same rules as the product-selection page: show an error when the images to optimize
  // exceed the remaining credits instead of starting a job that would fail.
  const [checkingCredits, setCheckingCredits] = useState(false);

  const handleOptimizeClick = useCallback(async () => {
    setCheckingCredits(true);
    try {
      let remaining = null; // null = balance unavailable, the server still enforces credits
      try {
        const balRes = await authenticatedFetchRef.current('/api/credits/balance');
        const balData = await balRes.json();
        if (balData.success) remaining = balData.data?.remainingCredits ?? 0;
      } catch { /* non-critical: the server still enforces credits */ }

      if (remaining !== null) {
        if (remaining <= 0) {
          showErrorToast('You have 0 credits remaining. Please purchase more credits before optimizing.');
          return;
        }
        if (unoptimizedImageCount > remaining) {
          showErrorToast(`Credit limit reached. ${unoptimizedImageCount} images are left to optimize but you only have ${remaining} credits. Purchase more credits to continue.`);
          return;
        }
      }
    } finally {
      setCheckingCredits(false);
    }

    await handleStartOptimization();
  }, [unoptimizedImageCount, handleStartOptimization]);

  // Shared "Optimize" button (shown in different places depending on state)
  const optimizeButton = (label) => (
    <s-button
      variant="primary"
      size="large"
      onClick={handleOptimizeClick}
      disabled={optimizing || checkingCredits || !hasSyncedProducts}
      loading={optimizing || checkingCredits}
    >
      {label}
    </s-button>
  );

  if (error) {
    return (
      <s-section>
        <s-banner tone="critical" dismissible onDismiss={clearError}>
          <s-text>{error}</s-text>
        </s-banner>
      </s-section>
    );
  }

  return (
    <s-section>
      <s-stack gap="base">

        {/* Page Header */}
        <s-stack direction="inline" gap="base" justifyContent="space-between" alignItems="start">
          <s-stack gap="small">
            <s-heading>Dashboard</s-heading>
            <s-text color="subdued">Overview of image optimization across your store</s-text>
          </s-stack>
          <CreditsWidget refreshKey={creditsRefreshKey} onPurchase={() => navigate('/pricing')} />
        </s-stack>

        {/* Paused banner */}
        {syncData?.syncState === 'paused' && (
          <s-banner tone="warning">
            <s-stack direction="inline" gap="small" alignItems="center" justifyContent="space-between">
              <s-text type="strong">
                Product sync is paused.
              </s-text>
              <s-text tone="subdued" size="small">Click Resume Sync to continue from where it stopped.</s-text>
            </s-stack>
          </s-banner>
        )}

        {/* Syncing banner */}
        {syncData?.isProductSyncRunning && (
          <s-banner tone="info">
            <s-stack direction="inline" gap="small" alignItems="center" justifyContent="space-between">
              <s-text type="strong">Syncing products from your Shopify store…</s-text>
              <s-text tone="subdued" size="small">
                Stats and product counts will update automatically once sync completes.
              </s-text>
            </s-stack>
          </s-banner>
        )}

        {/* Product Sync Status */}
        <s-box padding="base" background="subdued" border="base" borderRadius="base">
          <s-stack gap="small">
            <s-stack direction="inline" gap="base" justifyContent="space-between" alignItems="center">
              <s-stack direction="inline" gap="small" alignItems="center">
                <s-text type="strong">Product Sync Status</s-text>
                {syncData?.isProductSyncRunning && <s-badge tone="info">Syncing…</s-badge>}
                {!syncData && syncLoading && <s-badge tone="attention">Checking…</s-badge>}
              </s-stack>
              <s-stack direction="inline" gap="small" alignItems="center">
                {/* HOTFIX: show Pause when ANY signal says a sync may be active: this server's sync state,
                    the database "sync running" flag, or the dashboard still polling sync-status. If the
                    sync is stuck, Pause clears the stale flag. */}
                {syncData && syncData.syncState !== 'pausing' && syncData.syncState !== 'paused' && (
                  syncData.syncState === 'running' || syncData.isProductSyncRunning === true || syncPolling
                ) && (
                  <s-button variant="secondary" icon="pause-circle" onClick={handlePauseSync}>
                    Pause Sync
                  </s-button>
                )}
                {syncData?.syncState === 'pausing' && (
                  <s-button variant="secondary" icon="pause-circle" disabled loading>
                    Pausing…
                  </s-button>
                )}
                {syncData?.syncState === 'paused' && (
                  <s-button variant="primary" icon="play-circle" onClick={handleResumeSync}>
                    Resume Sync
                  </s-button>
                )}
                {/* Hidden while a sync is active (running, pausing or paused: Pause / Resume are shown instead) */}
                {!(syncData?.isProductSyncRunning === true || syncPolling
                  || ['running', 'pausing', 'paused'].includes(syncData?.syncState)) && (
                  <s-button variant="primary" icon="refresh" onClick={handleResyncProducts}>
                    Re-Sync Products
                  </s-button>
                )}
                {stats && !statsLoading && optimizedCount === 0 && unoptimizedCount > 0 && (
                  optimizeButton(`Optimize Synced Products (${unoptimizedCount})`)
                )}
              </s-stack>
            </s-stack>
            <s-stack direction="inline" gap="large" justifyContent="space-between" alignItems="center">
              <s-stack gap="small">
                <s-text color="subdued">Products Synced (active in store with at least 1 image)</s-text>
                <s-heading>
                  {syncData === null ? '—'
                    : syncData?.totalInShopify != null
                      ? `${syncData.totalSynced || 0} / ${syncData.totalInShopify}`
                      : `${syncData?.totalSynced || 0}`}
                </s-heading>
              </s-stack>
              <s-stack gap="small" alignment="end">
                <s-text color="subdued">Remaining</s-text>
                <s-heading>
                  {syncData === null ? '—'
                    : syncData?.totalInShopify != null
                      ? Math.max(0, (syncData.totalInShopify || 0) - (syncData.totalSynced || 0))
                      : '—'}
                </s-heading>
              </s-stack>
            </s-stack>
          </s-stack>
        </s-box>

        {/* Optimization Statistics */}
        <s-box padding="base" background="subdued" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-stack direction="inline" gap="base" justifyContent="space-between" alignItems="center">
              <s-heading>Optimization Statistics</s-heading>
              {stats && !statsLoading && optimizedCount > 0 && unoptimizedCount > 0 && (
                optimizeButton(`Optimize Remaining Products (${unoptimizedCount})`)
              )}
            </s-stack>

            {/* Store Products */}
            <s-stack gap="base">
              <s-heading size="small">Store Products</s-heading>
              <s-grid gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap="base">
                <MetricCard label="Unoptimized Products" value={products?.unoptimized || 0} isLoading={statsLoading} />
                <MetricCard label="Fully Optimized" value={products?.optimized || 0} isLoading={statsLoading} />
                <s-box padding="base" background="base" border="base" borderRadius="base">
                  <s-stack gap="small">
                    <s-stack direction="inline" gap="small" alignItems="center">
                      <s-text color="subdued">Partially Optimized</s-text>
                      <s-icon type="info" interestFor="partial-opt-tip"></s-icon>
                      <s-tooltip id="partial-opt-tip">Products with some images optimized and some not. This can happen due to a server error, because certain images could not be compressed further, or because some individual images were reverted after optimization.</s-tooltip>
                    </s-stack>
                    {statsLoading ? <div className="shimmer-loader shimmer-heading" /> : <s-heading>{products?.partiallyOptimized || 0}</s-heading>}
                  </s-stack>
                </s-box>
                <MetricCard label="Total Products" value={products?.total || 0} isLoading={statsLoading} />
              </s-grid>
            </s-stack>

            <s-divider />

            {/* Store Product Images */}
            <s-stack gap="base">
              <s-heading size="small">Store Product Images</s-heading>
              <s-grid gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap="base">
                <MetricCard label="Unoptimized Images" value={images?.unoptimized || 0} isLoading={statsLoading} />
                <MetricCard label="Optimized Images" value={images?.optimized || 0} isLoading={statsLoading} />
                <MetricCard label="Total Images" value={images?.total || 0} isLoading={statsLoading} />
              </s-grid>
            </s-stack>

            <s-divider />

            {/* Lifetime Statistics */}
            <s-stack gap="base">
              <s-heading size="small">Lifetime Statistics</s-heading>
              <s-grid gridTemplateColumns="repeat(5, 1fr)" gap="base">
                <MetricCard label="Store Optimizations" value={lifetime?.storeImages || 0} isLoading={statsLoading} />
                <MetricCard label="Upload Optimizations" value={lifetime?.manualUploads || 0} isLoading={statsLoading} />
                <MetricCard label="Original Size" value={`${savings?.totalOriginalMb?.toFixed(2) || 0} MB`} isLoading={statsLoading} />
                <MetricCard label="Optimized Size" value={`${savings?.totalOptimizedMb?.toFixed(2) || 0} MB`} isLoading={statsLoading} />
                <MetricCard label="Total Savings" value={`${savings?.totalSavedMb?.toFixed(2) || 0} MB`} isLoading={statsLoading} />
              </s-grid>
            </s-stack>
          </s-stack>
        </s-box>

        {/* Starting job feedback */}
        {optimizing && <s-banner tone="info">Starting optimization job, please wait…</s-banner>}

        {/* Job Processing UI */}
        <div id="dashboard-jobs-section">
          <JobProgressSection onStatsRefresh={refreshStats} onCreditChange={handleCreditChange} />
        </div>

      </s-stack>
    </s-section>
  );
}

// ─── Scroll helpers ──────────────────────────────────────────────────────────

function scrollToElement(el) {
  if (!el) return;
  try {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const rect = el.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
    const target = scrollTop + rect.top - 80;
    if (target > 0) window.scrollTo({ top: target, behavior: 'smooth' });
  } catch { /* scroll failed — non-critical */ }
}

function scrollToJobCard(jobId, maxAttempts = 25) {
  let attempt = 0;
  const tryScroll = () => {
    attempt++;
    const card = jobId ? document.querySelector(`[data-job-card-id="${jobId}"]`) : null;
    if (card) { scrollToElement(card); return; }
    const section = document.getElementById('dashboard-jobs-section');
    if (attempt >= maxAttempts && section) { scrollToElement(section); return; }
    if (attempt < maxAttempts) setTimeout(tryScroll, 200);
  };
  setTimeout(tryScroll, 2000);
}

// ─── JobProgressSection ──────────────────────────────────────────────────────

function JobProgressSection({ onStatsRefresh = null, onCreditChange = null }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRef = useRef(authenticatedFetch);
  useEffect(() => { fetchRef.current = authenticatedFetch; }, [authenticatedFetch]);

  const jobsRef = useRef([]);
  const intervalRef = useRef(null);
  const shouldPollRef = useRef(true);
  const fetchInFlightRef = useRef(false);
  const hasEverHadJobsRef = useRef(false);
  const optimisticJobIdRef = useRef(null);
  const prevStatusesRef = useRef(new Map());

  // ── Merge helper ─────────────────────────────────────────────────────────
  const mergeJobs = useCallback((apiJobs) => {
    const map = new Map();
    jobsRef.current.forEach(j => map.set(j.id, j));
    apiJobs.forEach(j => map.set(j.id, j));
    const merged = Array.from(map.values())
      .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))
      .slice(0, 2);
    jobsRef.current = merged;
    if (merged.length > 0) hasEverHadJobsRef.current = true;
    setJobs(merged);
    return merged;
  }, []);

  // ── Fetch active jobs ────────────────────────────────────────────────────
  const fetchActiveJobs = useCallback(async () => {
    // Skip this tick if the previous request is still pending, otherwise slow
    // responses make setInterval stack up overlapping requests.
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    // A request that never answers must not block the polling forever: give up on it after 15 s (next tick retries)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetchRef.current('/api/active-jobs', { signal: controller.signal });
      if (!res.ok) { setLoading(false); return; }
      const data = await res.json();
      if (!data.jobs || !Array.isArray(data.jobs)) { setLoading(false); return; }

      if (data.jobs.length > 0) hasEverHadJobsRef.current = true;
      setLoading(false);

      const apiJobs = data.jobs
        .filter(j => j?.id)
        .map(j => ({
          id: j.id,
          status: j.status || 'pending',
          startTime: j.startTime || (j.started_at ? new Date(j.started_at).getTime() : Date.now()),
        }));

      const activeJobs = apiJobs.filter(j => j.status === 'pending' || j.status === 'running');

      // Clear optimistic flag once API confirms the job
      if (optimisticJobIdRef.current && apiJobs.some(j => j.id === optimisticJobIdRef.current)) {
        optimisticJobIdRef.current = null;
      }

      // Stop polling when no active jobs remain
      if (activeJobs.length === 0 && !optimisticJobIdRef.current) {
        if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
        shouldPollRef.current = false;
      } else if (!intervalRef.current) {
        shouldPollRef.current = true;
        intervalRef.current = setInterval(() => {
          if (!shouldPollRef.current || document.hidden) return;
          fetchActiveJobs();
        }, ACTIVE_JOBS_POLL_MS);
      }

      // Detect job completion → refresh stats
      const hadActive = jobsRef.current.some(j => j.status === 'pending' || j.status === 'running');
      mergeJobs(apiJobs);
      if (hadActive && activeJobs.length === 0 && onStatsRefresh) {
        try { onStatsRefresh(); } catch { /* non-critical */ }
      }

      apiJobs.forEach(j => prevStatusesRef.current.set(j.id, j.status));
    } catch {
      setLoading(false);
    } finally {
      clearTimeout(timeoutId);
      fetchInFlightRef.current = false;
    }
  }, [mergeJobs, onStatsRefresh]);

  // ── Init: handle nav state (optimistic job from Crush page) ──────────────
  useEffect(() => {
    const navState = location.state;
    const fromState = navState?.wantScrollToJobs === true;
    const fromStorage = (() => {
      const ts = sessionStorage.getItem('wantScrollToJobs');
      return !!(ts && (Date.now() - Number(ts)) < 15000);
    })();
    if (!fromState && !fromStorage) return;

    const pendingJobId = navState?.pendingJobId || sessionStorage.getItem('pendingJobId') || null;
    sessionStorage.removeItem('wantScrollToJobs');
    sessionStorage.removeItem('pendingJobId');
    if (fromState) navigate(location.pathname + location.search, { replace: true, state: null });

    if (pendingJobId) {
      hasEverHadJobsRef.current = true;
      optimisticJobIdRef.current = pendingJobId;
      const opt = { id: pendingJobId, status: 'pending', startTime: Date.now() };
      jobsRef.current = [opt];
      setJobs([opt]);
      setLoading(false);
    }
    scrollToJobCard(pendingJobId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Listen for jobStarted events (from Dashboard optimize button) ────────
  useEffect(() => {
    const handler = (e) => {
      const { jobId } = e.detail;
      if (!intervalRef.current) {
        shouldPollRef.current = true;
        intervalRef.current = setInterval(() => {
          if (!shouldPollRef.current || document.hidden) return;
          fetchActiveJobs();
        }, ACTIVE_JOBS_POLL_MS);
      }
      setJobs(prev => {
        if (prev.some(j => j.id === jobId)) return prev;
        hasEverHadJobsRef.current = true;
        const updated = [...prev, { id: jobId, status: 'pending', startTime: Date.now() }]
          .sort((a, b) => (b.startTime || 0) - (a.startTime || 0))
          .slice(0, 2);
        jobsRef.current = updated;
        return updated;
      });
      scrollToJobCard(jobId);
    };
    window.addEventListener('jobStarted', handler);
    return () => window.removeEventListener('jobStarted', handler);
  }, [fetchActiveJobs]);

  // ── Start polling on mount ───────────────────────────────────────────────
  useEffect(() => {
    fetchActiveJobs();
    intervalRef.current = setInterval(() => {
      if (!shouldPollRef.current || document.hidden) return;
      fetchActiveJobs();
    }, ACTIVE_JOBS_POLL_MS);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchActiveJobs]);

  // ── Stop all jobs (two-click confirm; completed jobs / optimized images stay) ──
  const [confirmingStop, setConfirmingStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const confirmTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(confirmTimerRef.current), []);

  const handleStopAllJobs = useCallback(async () => {
    if (!confirmingStop) {
      setConfirmingStop(true);
      clearTimeout(confirmTimerRef.current);
      confirmTimerRef.current = setTimeout(() => setConfirmingStop(false), 5000);
      return;
    }
    clearTimeout(confirmTimerRef.current);
    setConfirmingStop(false);
    setStopping(true);
    try {
      const res = await fetchRef.current('/api/stop-all-jobs', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showErrorToast(data.message || 'Failed to stop jobs');
        return;
      }
      showSuccessToast(data.message || 'Jobs stopped');
      // Drop the local job list (mergeJobs would otherwise keep deleted jobs) and re-fetch
      jobsRef.current = [];
      setJobs([]);
      fetchActiveJobs();
      setTimeout(fetchActiveJobs, 1500); // covers a refresh skipped by the in-flight guard
    } catch (err) {
      showErrorToast(err.message || 'Failed to stop jobs');
    } finally {
      setStopping(false);
    }
  }, [confirmingStop, fetchActiveJobs]);

  const jobIds = jobs.map(j => j.id).filter(Boolean);
  // Only worth showing when there is something to stop (or delete: failed jobs); also while the list is still loading
  const hasStoppableJobs = jobs.some(j => ['pending', 'running', 'failed'].includes(j.status));

  return (
    <s-box padding="base" background="base" border="base" borderRadius="base">
      {/* Rendered outside OptimizationProgress so it stays visible during the loading shimmer */}
      {(loading || hasStoppableJobs || stopping) && (
        <s-stack direction="inline" justifyContent="end" gap="base">
          <s-button
            variant="secondary"
            tone="critical"
            onClick={handleStopAllJobs}
            disabled={stopping}
            loading={stopping}
          >
            {confirmingStop ? 'Click again to confirm' : 'Stop all running jobs'}
          </s-button>
        </s-stack>
      )}
      <OptimizationProgress
        jobIds={jobIds}
        hasEverHadJobs={hasEverHadJobsRef.current}
        isLoading={loading}
        onCreditChange={onCreditChange}
      />
    </s-box>
  );
}

export default Dashboard;
